import { Component, computed, input, output, signal } from '@angular/core';
import { Button } from 'primeng/button';
import { TabViewChangeEvent, TabViewModule } from 'primeng/tabview';

import { GridPaginatorRows } from '@ppg/core/constants';
import { ResourceItem } from '@ppg/core/enums';
import { Color, Option } from '@ppg/core/models';
import { ColorsGridComponent } from '@ppg/shared/color/colors-grid';
import { OptionSelectorSidebarComponent } from '@ppg/shared/components';
import { ResourceValuePipe } from '@ppg/shared/resource';

import { ColorInfoGroup } from '../../models/color-group.model';

@Component({
  selector: 'ppg-color-family-grouping',
  standalone: true,
  imports: [TabViewModule, Button, OptionSelectorSidebarComponent, ColorsGridComponent, ResourceValuePipe],
  templateUrl: './color-family-grouping.component.html',
})
export class ColorFamilyGroupingComponent {
  readonly filterOptions = input.required<Option[]>();
  readonly colorGroups = input.required<ColorInfoGroup[] | null>();
  readonly paginatorHidden = input<boolean>(true);

  readonly colorInfoGroupChanged = output<ColorInfoGroup | null>();

  readonly filteredColorGroups = computed(() => this.colorGroups()?.filter((group) => group.colors.length > 0));
  readonly rowsPerPage = computed(() =>
    this.paginatorHidden() ? this.selectedColorGroup()?.colors.length : GridPaginatorRows.twenty,
  );
  readonly activeIndex = signal(0);
  readonly selectedColorGroup = computed(() => {
    const groups = this.filteredColorGroups();
    const activeIndex = this.activeIndex();
    return this.#getGroupForIndex(activeIndex, groups);
  });
  readonly resources = {
    filterHeader: ResourceItem.COLORS_FAVORITES_FILTER_SUBTITLE,
  };

  readonly favoriteColorAdded = output<Color>();
  readonly favoriteColorRemoved = output<Color>();
  readonly colorDetailsLinkClicked = output<Color>();

  showOptionSelector = false;

  selectedTabChanged($event: TabViewChangeEvent) {
    this.activeIndex.set($event.index);
    this.#emitSelectedColorGroupChange($event.index);
  }

  onFilterValueChange(option: Option) {
    this.activeIndex.set(Number(option.id));
    this.#emitSelectedColorGroupChange(Number(option.id));
  }

  onFavoriteColorAdded(color: Color) {
    this.favoriteColorAdded.emit(color);
  }

  onFavoriteColorRemoved(color: Color) {
    this.favoriteColorRemoved.emit(color);
  }

  onColorDetailsLinkClicked(color: Color): void {
    this.colorDetailsLinkClicked.emit(color);
  }

  #emitSelectedColorGroupChange(index: number) {
    const selectedColorGroup = this.#getGroupForIndex(index, this.filteredColorGroups());
    this.colorInfoGroupChanged.emit(selectedColorGroup);
  }

  #getGroupForIndex(index: number, groups: ColorInfoGroup[] | undefined) {
    return groups ? (groups[index] ? groups[index] : groups[0]) : null;
  }
}
